// Tap-to-share recipient view (Option B per SPEC-ALPHA-SDK-034 v0.2,
// SPEC-INFRA-LINK-SHARE-RECIPIENT-WEB-CONTRACT §4/§5).
//
// Flow:
//
//   1. Page loads at `link.blockhaven.health/s/{token}#k={cek}`
//   2. share.* GET /share/discover/{token} → kind = "webrtc"
//   3. Render "connecting" state
//   4. POST /share/sessions/by-token/{token}/join to declare a
//      recipient profile (optional display name + specialty); server
//      resolves token → session_id and hands back TWO session-bound
//      capabilities: `ws_bearer` (aud=ets-signaling) and
//      `bundle_bearer` (aud=share-bundle-service).
//   5. Open WebSocket `${SIGNALING}/signal?session={id}` with the
//      `ws_bearer` — this drives PRESENCE only (the patient sees the
//      viewer is live).
//   6. Poll share.* GET /share/sessions/{id}/status (bundle_bearer)
//      every ~2s for the AUTHORITATIVE state. ets-signaling stays an
//      opaque relay (NCI-3) — the server, not the WS, is the source of
//      truth for APPROVED/REVOKED/EXPIRED.
//   7. On APPROVED: GET /share/sessions/{id}/bundle (bundle_bearer) →
//      ChaChaPoly sealed box
//   8. WebCrypto decrypt with CEK from URL fragment
//   9. Trigger browser save of decrypted bundle bytes
//
// "Option B" = WebRTC for *presence only* + HTTPS for the bundle +
// URL-fragment CEK. NO RTCPeerConnection anywhere in this flow.
// Option A's full peer-to-peer datachannel streaming is a planned
// post-Alpha evolution.

import type { DiscoverResponse } from "./discover.js";
import { decryptBundle, extractCEKFromHash } from "./crypto.js";
import { SignalingClient, type ShareSessionState } from "./signaling.js";

export interface ShareSessionViewProps {
  apiBaseUrl: string;
  token: string;
  metadata: DiscoverResponse;
}

// Two-token join response (contract §4). `ws_bearer` is aud=ets-signaling
// for the WS presence channel; `bundle_bearer` is aud=share-bundle-service
// for the /status poll and the /bundle download.
interface JoinResponse {
  session_id: string;
  ws_bearer: string;
  ws_bearer_expires_at: string;
  bundle_bearer: string;
  bundle_bearer_expires_at: string;
}

// Authoritative state poll response (contract §5).
interface StatusResponse {
  state: ShareSessionState;
}

const STATUS_POLL_INTERVAL_MS = 2000;

/**
 * Lifetime of `ws_bearer` and `bundle_bearer`. share-bundle-service mints
 * both with a 300 s TTL (src/routes/sessions.rs) and this page has no
 * refresh, so after this long neither the socket nor `/status` can
 * authenticate. Age is measured from when the join request was sent,
 * which is no later than the mint.
 */
export const BEARER_TTL_MS = 300_000;
const TERMINAL_STATES: ReadonlySet<ShareSessionState> = new Set([
  "DOWNLOADED",
  "CLOSED",
  "REVOKED",
  "EXPIRED",
]);

/**
 * Bootstrap the recipient flow. Renders an evolving view that walks
 * the recipient through connecting → awaiting approval → downloading →
 * decrypted/saved. Errors at any step render an inline error card.
 */
export async function renderShareSessionView(
  root: HTMLElement,
  props: ShareSessionViewProps,
): Promise<void> {
  const cek = extractCEKFromHash(window.location.hash);
  if (!cek) {
    renderError(
      root,
      "Missing share key",
      "This URL doesn't include the encryption key. The link may have been edited or copied incorrectly.",
    );
    return;
  }

  // The stage card and the connection notice are separate elements so the
  // /status poll, which re-renders the stage every tick, never hides the
  // notice, and the notice never replaces the stage.
  root.innerHTML = "";
  const stageEl = document.createElement("div");
  const noticeEl = document.createElement("div");
  noticeEl.hidden = true;
  root.append(stageEl, noticeEl);

  renderStage(stageEl, "connecting", { expiresAt: props.metadata.expires_at });

  const joinSentAt = Date.now();
  let joinResp: JoinResponse;
  try {
    joinResp = await joinSession(props.apiBaseUrl, props.token);
  } catch (e) {
    // The join token is one-shot: share-bundle-service answers 409 once the
    // session has left CREATED, and 410 once it has expired. Reloading
    // cannot help with either.
    const status = e instanceof HttpStatusError ? e.status : 0;
    if (status === 409) {
      renderError(
        stageEl,
        "This link has already been opened",
        "Ask the patient to share it again.",
      );
    } else if (status === 410) {
      renderError(
        stageEl,
        "This share has expired",
        "Ask the patient to share with you again if you still need their records.",
      );
    } else {
      renderError(
        stageEl,
        "Couldn't connect to this share",
        "Check your connection and reload the page to try again.",
      );
    }
    return;
  }

  // WS is for PRESENCE only — connecting it is what makes the patient
  // see a live viewer (so they can Approve). We do NOT drive the
  // approval/revoke logic off relay frames; the server is the truth.
  //
  // Once the bearers can no longer authenticate (the socket gives up after
  // BEARER_TTL_MS, or `/status` or the download answers 401/403), stop
  // polling and the socket and tell the recipient the page has timed out.
  // The join token is one-shot, so a reload cannot mint fresh bearers; the
  // patient has to share again.
  //
  // Once the download has started, presence no longer matters: a socket
  // give-up then is ignored, and only the download's own 401/403 can
  // time the page out.
  let ended = false;
  let downloadStarted = false;
  let stopPolling = (): void => {};
  const timedOut = (): void => {
    if (ended) return;
    ended = true;
    stopPolling();
    presence.stop();
    renderTimedOut(stageEl);
  };

  const wsUrl = buildWebSocketUrl(joinResp.session_id);
  const presence = startPresence(
    noticeEl,
    new SignalingClient(wsUrl, joinResp.ws_bearer),
    {
      bearerExpired: () => Date.now() - joinSentAt >= BEARER_TTL_MS,
      onBearerExpired: timedOut,
      quiet: () => downloadStarted,
    },
  );

  // Authoritative state via polling (contract §5). Drives the
  // awaiting-approval render + the APPROVED/REVOKED/EXPIRED transitions.
  // Runs whether or not the presence socket is connected.
  stopPolling = pollStatus(stageEl, props, joinResp, cek, presence, {
    onAuthRejected: timedOut,
    onDownloadStarted: () => {
      downloadStarted = true;
      // A connection notice already on screen says the page keeps checking
      // for approval, which is no longer true.
      hideNotice(noticeEl);
    },
  });
}

interface Presence {
  /** Close the socket and remove the connection notice. */
  stop(): void;
}

export interface PresenceOptions {
  /** True once `ws_bearer` is past its TTL. */
  bearerExpired: () => boolean;
  /** Called instead of offering Try again when the bearer has expired. */
  onBearerExpired: () => void;
  /**
   * True once the page no longer needs presence (the download has
   * started). A give-up then shows nothing and calls nothing.
   */
  quiet: () => boolean;
}

/**
 * Connect the presence socket. The client emits `error` only when its
 * own reconnect budget runs out; relayed frames never reach this
 * listener. On that give-up, show a connection notice whose Try again
 * starts one more bounded connection cycle. A reconnect cannot succeed
 * once the bearer has expired, so a give-up (or a Try again) after that
 * calls `onBearerExpired`, which ends the page. Once `quiet()` is true a
 * give-up is ignored.
 */
export function startPresence(
  noticeEl: HTMLElement,
  signaling: SignalingClient,
  options: PresenceOptions,
): Presence {
  let stopped = false;
  signaling.on((event) => {
    if (stopped) return;
    if (event.type === "error") {
      if (options.quiet()) return;
      if (options.bearerExpired()) {
        options.onBearerExpired();
        return;
      }
      renderConnectionProblem(noticeEl, () => {
        if (stopped || options.quiet()) return;
        if (options.bearerExpired()) {
          options.onBearerExpired();
          return;
        }
        hideNotice(noticeEl);
        signaling.connect();
      });
    } else if (event.type === "connected") {
      hideNotice(noticeEl);
    }
  });
  signaling.connect();
  return {
    stop(): void {
      stopped = true;
      signaling.disconnect();
      hideNotice(noticeEl);
    },
  };
}

interface PollHooks {
  /** `/status` answered 401/403, or the download did. */
  onAuthRejected: () => void;
  /** `/status` reported APPROVED and the download is starting. */
  onDownloadStarted: () => void;
}

/**
 * Poll share.* `/status` for the authoritative session state every
 * ~2s. On APPROVED, download + decrypt. On any terminal state, stop
 * polling and tear the WS down. Transient poll failures are tolerated
 * (the next tick retries) so a single blip doesn't kill the flow. A 401
 * or 403 means the bearer no longer authenticates: stop and call
 * `hooks.onAuthRejected`. Returns a function that stops polling.
 */
function pollStatus(
  root: HTMLElement,
  props: ShareSessionViewProps,
  joinResp: JoinResponse,
  cek: Uint8Array,
  presence: Presence,
  hooks: PollHooks,
): () => void {
  let stopped = false;
  let approvedHandled = false;

  const stop = (): void => {
    stopped = true;
    if (timer !== null) {
      window.clearInterval(timer);
      timer = null;
    }
  };

  const tick = async (): Promise<void> => {
    if (stopped) return;
    let state: ShareSessionState;
    try {
      state = await fetchStatus(props.apiBaseUrl, joinResp);
    } catch (e) {
      if (stopped) return;
      if (isAuthRejection(e)) {
        stop();
        hooks.onAuthRejected();
      }
      // Otherwise transient — let the next interval retry.
      return;
    }
    if (stopped) return;

    if (state === "APPROVED") {
      if (!approvedHandled) {
        approvedHandled = true;
        hooks.onDownloadStarted();
        stop();
        void downloadAndDecrypt(root, props, joinResp, cek, presence, hooks.onAuthRejected);
      }
      return;
    }

    if (state === "REVOKED" || state === "EXPIRED") {
      stop();
      presence.stop();
      renderError(
        root,
        state === "REVOKED" ? "Share revoked by patient" : "Share expired",
        "The patient has ended this share. Contact them if you still need access.",
      );
      return;
    }

    if (TERMINAL_STATES.has(state)) {
      // CLOSED / DOWNLOADED — nothing more to do here.
      stop();
      presence.stop();
      return;
    }

    renderStage(root, "awaiting-approval", {
      expiresAt: props.metadata.expires_at,
      currentState: state,
    });
  };

  let timer: number | null = window.setInterval(() => {
    void tick();
  }, STATUS_POLL_INTERVAL_MS);
  // Fire one immediately so the recipient isn't stuck on "connecting"
  // for a full interval.
  void tick();
  return stop;
}

/** A non-2xx response from share-bundle-service. */
class HttpStatusError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

function isAuthRejection(e: unknown): boolean {
  return e instanceof HttpStatusError && (e.status === 401 || e.status === 403);
}

async function fetchStatus(
  apiBaseUrl: string,
  joinResp: JoinResponse,
): Promise<ShareSessionState> {
  const resp = await fetch(
    `${apiBaseUrl}/share/sessions/${encodeURIComponent(joinResp.session_id)}/status`,
    {
      method: "GET",
      headers: { Authorization: `Bearer ${joinResp.bundle_bearer}` },
      credentials: "omit",
      cache: "no-store",
    },
  );
  if (!resp.ok) {
    throw new HttpStatusError(resp.status, `Status poll failed: HTTP ${resp.status}`);
  }
  const data = (await resp.json()) as StatusResponse;
  return data.state;
}

async function joinSession(
  apiBaseUrl: string,
  token: string,
): Promise<JoinResponse> {
  // Recipient holds the opaque join-token (NOT the session_id). The
  // server resolves token → session_id and mints two session-bound
  // capabilities (contract §4): `ws_bearer` (aud=ets-signaling) for the
  // presence WS and `bundle_bearer` (aud=share-bundle-service) for the
  // /status poll + /bundle download.
  const resp = await fetch(
    `${apiBaseUrl}/share/sessions/by-token/${encodeURIComponent(token)}/join`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "omit",
      // No profile fields. share-bundle-service's join_by_token treats
      // display_name and specialty as optional but rejects an empty string
      // (400 "display_name must be 1..=128 chars when present"), so an empty
      // display_name made every join fail. This page has no profile to send.
      body: JSON.stringify({}),
    },
  );
  if (!resp.ok) {
    throw new HttpStatusError(resp.status, `Join failed: HTTP ${resp.status}`);
  }
  return (await resp.json()) as JoinResponse;
}

function buildWebSocketUrl(sessionId: string): string {
  // ets-signaling's real route is `/signal?session=` with ring-hash
  // pairing (contract §5) on the dedicated `signaling.*` host — NOT the
  // old `/share/sessions/{id}/signal` path on the API host.
  return `${__SIGNALING_BASE__}/signal?session=${encodeURIComponent(sessionId)}`;
}

/**
 * The patient uploads the records only after approving, so a download
 * the moment `/status` says APPROVED can get 409 `no_bundle`. Retry that
 * briefly (share-bundle-service checks expiry and terminal states ahead of
 * `no_bundle`, so a retry stops on 410). Throws HttpStatusError otherwise.
 */
const DOWNLOAD_MAX_TRIES = 12;
const DOWNLOAD_RETRY_MS = 1000;

async function fetchSealedRecords(
  apiBaseUrl: string,
  joinResp: JoinResponse,
): Promise<Uint8Array> {
  for (let attempt = 1; ; attempt++) {
    const resp = await fetch(
      `${apiBaseUrl}/share/sessions/${encodeURIComponent(joinResp.session_id)}/bundle`,
      {
        method: "GET",
        headers: { Authorization: `Bearer ${joinResp.bundle_bearer}` },
        credentials: "omit",
      },
    );
    if (resp.ok) return new Uint8Array(await resp.arrayBuffer());
    if (resp.status !== 409 || attempt >= DOWNLOAD_MAX_TRIES) {
      throw new HttpStatusError(resp.status, `Download failed: HTTP ${resp.status}`);
    }
    await new Promise((r) => setTimeout(r, DOWNLOAD_RETRY_MS));
  }
}

async function downloadAndDecrypt(
  root: HTMLElement,
  props: ShareSessionViewProps,
  joinResp: JoinResponse,
  cek: Uint8Array,
  presence: Presence,
  onAuthRejected: () => void,
): Promise<void> {
  renderStage(root, "downloading", { expiresAt: props.metadata.expires_at });

  let sealed: Uint8Array;
  try {
    sealed = await fetchSealedRecords(props.apiBaseUrl, joinResp);
  } catch (e) {
    if (isAuthRejection(e)) {
      onAuthRejected();
      return;
    }
    const status = e instanceof HttpStatusError ? e.status : 0;
    if (status === 410) {
      renderError(
        root,
        "This share has ended",
        "Ask the patient to share with you again if you still need their records.",
      );
    } else if (status === 409) {
      renderError(
        root,
        "Nothing has been shared yet",
        "The patient's records haven't arrived. Ask the patient to share with you again.",
      );
    } else {
      renderError(
        root,
        "Couldn't download the records",
        "Check your connection, then ask the patient to share with you again.",
      );
    }
    presence.stop();
    return;
  }

  let plaintext: Uint8Array;
  try {
    plaintext = await decryptBundle(cek, sealed);
  } catch {
    // No error detail in the copy: decryption errors are technical.
    renderError(
      root,
      "Couldn't open the records",
      "The link may have been copied incorrectly. Ask the patient to share again.",
    );
    presence.stop();
    return;
  }

  triggerDownload(plaintext, "shared-records.zip");
  renderStage(root, "downloaded", {
    expiresAt: props.metadata.expires_at,
    byteLength: plaintext.length,
  });
  presence.stop();
}

function triggerDownload(bytes: Uint8Array, filename: string): void {
  const buf = bytes.slice().buffer;
  const blob = new Blob([buf], { type: "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ── View rendering ───────────────────────────────────────────────────

type Stage = "connecting" | "awaiting-approval" | "downloading" | "downloaded";

interface StageContext {
  expiresAt: string;
  currentState?: ShareSessionState;
  byteLength?: number;
}

function renderStage(root: HTMLElement, stage: Stage, ctx: StageContext): void {
  const expires = new Date(ctx.expiresAt).toLocaleString();
  let title: string;
  let body: string;
  let footnote = `Session expires ${expires}.`;

  switch (stage) {
    case "connecting":
      title = "Connecting to the patient's device…";
      body =
        "We're setting up a secure connection. The patient will see you've joined.";
      break;
    case "awaiting-approval":
      title = "Waiting for the patient to approve";
      body =
        ctx.currentState === "PRESENT"
          ? "The patient can see that you've joined. They'll tap Approve to share their records with you."
          : "Waiting for the patient's device to connect. Once it does, they'll see that you've joined and can tap Approve to share their records with you.";
      break;
    case "downloading":
      title = "Downloading the shared records…";
      body =
        "The records are unlocked in your browser. BlockHaven's servers never see what they contain.";
      break;
    case "downloaded":
      title = "Records downloaded";
      body = ctx.byteLength
        ? `${formatBytes(ctx.byteLength)} saved to your Downloads folder.`
        : "Saved to your Downloads folder.";
      footnote = "You can close this page now.";
      break;
  }

  root.innerHTML = `
    <div class="card">
      <h1>${escapeHtml(title)}</h1>
      <p>${escapeHtml(body)}</p>
      <p style="color:#6a7a77; font-size:12px;">${escapeHtml(footnote)}</p>
    </div>
  `;
}

function renderError(root: HTMLElement, title: string, body: string): void {
  root.innerHTML = `
    <div class="card error">
      <h1>${escapeHtml(title)}</h1>
      <p>${escapeHtml(body)}</p>
    </div>
  `;
}

function renderConnectionProblem(el: HTMLElement, onRetry: () => void): void {
  el.innerHTML = `
    <div class="card error">
      <h1>Connection problem</h1>
      <p>We're having trouble staying connected, so the patient may not see that you're here. This page keeps checking for their approval.</p>
      <div class="actions">
        <button type="button" class="primary">Try again</button>
      </div>
    </div>
  `;
  el.querySelector("button")?.addEventListener("click", onRetry, { once: true });
  el.hidden = false;
}

function renderTimedOut(el: HTMLElement): void {
  el.innerHTML = `
    <div class="card error">
      <h1>This page has timed out</h1>
      <p>Ask the patient to share with you again.</p>
    </div>
  `;
}

function hideNotice(el: HTMLElement): void {
  el.hidden = true;
  el.innerHTML = "";
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
